import { GridSortItem } from "@mui/x-data-grid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { SrFaxManagementApi } from "../../../services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  useBaseContent,
} from "../../../utils/utils";

interface Props {
  pagination: DGT_Pagination;
  gridSorting: GridSortItem[];
  active?: boolean;
  archive?: boolean;
  faxNumber?: string;
  searchString?: string;
  read?: boolean;
  xTENANTID?: string;
  enabled?: boolean;
}

/**
 * @author [atish bagate]
 * @description [This hook is used to fetch the fax incoming list]
 *
 * @param {DGT_Pagination} pagination - The pagination object
 * @param {GridSortItem[]} gridSorting - The grid sorting object
 * @param {boolean} active - The active status
 * @param {boolean} archive - The archive status
 * @param {string} searchString - The search string
 * @param {string} xTENANTID - The xTENANTID
 * @returns {useBaseContent<any>} - The fax incoming list
 */
export const useFaxIncomingList = ({
  pagination,
  gridSorting,
  active,
  archive,
  searchString,
  faxNumber,
  read,
  xTENANTID,
  enabled = true,
}: Props): useBaseContent<any> => {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey: searchString,
    prevPage,
  });
  const pageSize = getPageSize(searchString, pagination);

  const sortDirect = gridSorting?.[0]?.sort ?? undefined;
  const sortBy = sortDirect ? "firstName" : undefined;

  return useQuery({
    queryKey: [
      "fax-incoming-list",
      pageIndex,
      pageSize,
      gridSorting,
      active,
      archive,
      read,
      searchString,
      faxNumber,
      xTENANTID,
    ],
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: !!xTENANTID && enabled,
    queryFn: async () =>
      await new SrFaxManagementApi(getConfigObject())
        .getAllFaxes1(
          pageIndex,
          pageSize,
          sortBy,
          sortDirect,
          archive,
          read,
          faxNumber,
          searchString,
          xTENANTID
        )
        .then((res) => res?.data),
  });
};
